import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    useShellAlert,
    useUpdateShellPrimary,
    useUpdateShellSecondary
} from '../../shell/hooks/shellHooks.js';

import {
    ConfigurationProvider,
    useCurationConfigValue,
    useGetFocusedInstanceIdValue,
    useSetCurationConfigState,
    useUpdateProps
} from '../../../hooks/widgetConfiguration.js';

import useListeningSearchTracking from '../hooks/useListeningSearchTracking.js';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus,
    useGetInstanceLoadingStatuses
} from '../../../hooks/loadingStatusHooks.js';

import {
    SavedTopicContext
} from '../contexts.js';

import {
    ExportHeaders
} from '../../../components/ExportHeaders.jsx';

import RIQLoading from '../../../components/RIQLoading.jsx';
import RIQPageError from '../../../components/RIQPageError.jsx';

import ExportDropdown from '../../../components/ExportDropdown.jsx';

import useMetrics from '../../../hooks/useMetrics.js';
import useLandscapeChangeRedirectUrl from '../../../hooks/useLandscapeChangeRedirectUrl.js';
import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    Loading
} from '../../../components/MetricLoadingView.jsx';

import {
    useVerifySearch,
    ValidationStates
} from '../hooks/useVerifySearch.js';

import {
    useIsExportingPDF,
    useIsExporting
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useLoadLandscapeListeningQuery,
    useUpdateListeningQueryLastViewed,
    useGetListeningQueryDisplayName,
    useGetListeningQueryTimePeriod
} from '../hooks/useListeningQueryPersistence.js';

import useListeningSearchTimePeriod from '../hooks/useListeningSearchTimePeriod.js';
import {
    useShowEditSavedSearchTour
} from '../hooks/useShowTours.js';

import {
    GeneratingQueryModal,
    useGenerateCompanyQuery
} from '../components/SearchRecommendations.jsx';

import {
    useGetDashboardType,
    DashboardTypes
} from '../hooks/useGetDashboardType.js';

import {
    listeningDefinitions
} from '../components/enums/listeningDefinitions.jsx';

import NoSearchTerm from '../components/NoSearchTerm.jsx';
import Toolbar from '../components/Toolbar.jsx';
import GlanceWidget from '../components/GlanceWidget.jsx';
import InstantSearchUpsellWidget from '../components/InstantSearchUpsellWidget.jsx';
import PostsTable from '../components/PostsTable.jsx';
import DimensionAnalysisWidget from '../components/DimensionAnalysisWidget.jsx';
import ActivitySentimentWidget from '../components/ActivitySentimentWidget.jsx';
import PositiveNegativeChartWidget from '../components/PositiveNegativeChartWidget.jsx';
import PositiveNegativeTableWidget from '../components/PositiveNegativeTableWidget.jsx';
import useGetCurationTitle from '../hooks/useGetCurationTitle.js';
import SourceWidget from '../components/SourceWidget.jsx';
import SearchWizard from '../components/savedSearch/SearchWizard.jsx';

import {
    InstantSearchInfoModal,
    useSetShowInstantSearchModal
} from '../components/InstantSearchInfoModal.jsx';

import GeneratingResultsModal from '../components/GeneratingResultsModal.jsx';

import useHasCompanionMode from '../hooks/useHasCompanionMode.js';
import useHasValidQuidMonitorToken from '../hooks/useHasValidQuidMonitorToken.js';
import InvalidQuidMonitorTokenErrorPage from '../components/InvalidQuidMonitorTokenErrorPage.jsx';

const widgets = [
    {
        isInitialLoadWidgetGroup: true,
        widgets: [{
            instanceId: 'glance-widget',
            widgetClass: GlanceWidget,
            props: {
                showDeltas: false,
                showSparklines: true
            }
        }]
    },
    {
        isInitialLoadWidgetGroup: true,
        onlyWithoutFullInstantSearch: true,
        onlyExportWithFullInstantSearch: true,
        widgets: [{
            instanceId: 'upsell-widget',
            widgetClass: InstantSearchUpsellWidget
        }]
    },
    {
        isInitialLoadWidgetGroup: true,
        widgets: [
            {
                instanceId: 'activity-sentiment',
                widgetClass: ActivitySentimentWidget
            },
            {
                instanceId: 'phrase-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'phrase',
                    allowedWithoutFullInstantSearch: true
                }
            }
        ]
    },
    {
        onlyExportWithFullInstantSearch: true,
        widgets: [
            {
                instanceId: 'positive-negative',
                widgetClass: PositiveNegativeChartWidget
            },
            {
                instanceId: 'sentiment-drivers',
                widgetClass: PositiveNegativeTableWidget
            }
        ]
    },
    {
        onlyExportWithFullInstantSearch: true,
        widgets: [
            {
                instanceId: 'source-chart',
                widgetClass: SourceWidget
            },
            {
                instanceId: 'domain-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'domain'
                }
            }
        ]
    },
    {
        onlyExportWithFullInstantSearch: true,
        widgets: [
            {
                instanceId: 'author-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'author'
                }
            },
            {
                instanceId: 'hashtag-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'hashtag'
                }
            }
        ]
    },
    {
        onlyExportWithFullInstantSearch: true,
        widgets: [
            {
                instanceId: 'person-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'person'
                }
            },
            {
                instanceId: 'brand-analysis',
                widgetClass: DimensionAnalysisWidget,
                props: {
                    dimension: 'brand'
                }
            }
        ]
    },
    {
        onlyExportWithFullInstantSearch: true,
        widgets: [{
            instanceId: 'posts-widget',
            widgetClass: PostsTable
        }]
    }
];

function getWidgetGroups(loadRemainingWidgets, hasFullInstantSearch, isSampleTopic, showOnlyInstanceId, isExporting) {

    const widgetGroups = _(widgets)
        .map(widgetGroup => {
            if (!loadRemainingWidgets && !widgetGroup.isInitialLoadWidgetGroup) {
                return;
            }

            if (widgetGroup.onlyWithoutFullInstantSearch && (isSampleTopic || hasFullInstantSearch || isExporting)) {
                return;
            }

            if (!isSampleTopic && !hasFullInstantSearch && widgetGroup.onlyWithFullInstantSearch && isExporting) {
                return;
            }

            if (showOnlyInstanceId) {
                const widgets = _.filter(widgetGroup.widgets, {
                    instanceId: showOnlyInstanceId
                });
                if (widgets.length) {
                    widgetGroup = _.defaults({
                        widgets: widgets
                    }, widgetGroup);
                } else {
                    return;
                }
            }

            return widgetGroup;
        })
        .compact()
        .value();

    return widgetGroups;
}

function getWidgetIds(widgetGroups) {
    const widgetIds = _.flatten(widgetGroups.map(widgetGroup => {
        return _.map(widgetGroup.widgets, 'instanceId');
    }));

    return widgetIds;
}

// This hook tracks views of the results page; it unforunately keeps some state in the global window object
// because we don't want to figure events on view more clicks (but our state is scoped to the curation lifecycle.)
function useTrackViewedResults(resetTrackingState, showDashboard) {
    const isExporting = useIsExporting();

    const companyId = useCurationConfigValue('companyId');

    const listeningQueryId = useCurationConfigValue('listeningQueryId');
    const {data: savedSearch} = useLoadLandscapeListeningQuery(listeningQueryId);
    const {updateLastViewed} = useUpdateListeningQueryLastViewed(listeningQueryId, savedSearch?.name, savedSearch?.definition);
    const isSampleTopic = Boolean(savedSearch?.isSampleTopic);

    const dashboardType = useGetDashboardType();

    useEffect(() => {
        if (resetTrackingState) {
            window.__listeningLastViewTracking = null;
        }

        if (isExporting || !showDashboard) {
            return;
        }

        const trackingOptions = {
            dashboardType: dashboardType
        };

        if (dashboardType === DashboardTypes.INSTANT_SEARCH) {
            trackingOptions.isGeneratedCompanyQuery = Boolean(companyId);
        }

        if (dashboardType === DashboardTypes.SAVED_SEARCH) {

            // if this is a saved search, we need to know if it's shared or not before we fire the tracking.
            if (listeningQueryId && !savedSearch) {
                return;
            }

            trackingOptions.isSampleTopic = isSampleTopic;
            trackingOptions.listeningQueryId = listeningQueryId;

        }

        if (_.isEqual(window.__listeningLastViewTracking, trackingOptions)) {
            return;
        }

        window.__listeningLastViewTracking = _.clone(trackingOptions);
        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ListeningViewedResults, trackingOptions);
    }, [companyId, dashboardType, isExporting, isSampleTopic, resetTrackingState, savedSearch, listeningQueryId, showDashboard]);

    useEffect(() => {
        // set Last Viewed for saved search if not sample topic
        if (!isSampleTopic && !isExporting) {
            updateLastViewed();
        }
    }, [isExporting, isSampleTopic, updateLastViewed]);
}

function ListeningSearchDashboard({exportTitle, topicName, timePeriodStr, widgetGroupsToRender, listeningQueryId, showOnlyInstanceId, isAnyWidgetLoaded}) {
    const isExporting = useIsExporting();
    const searchTerms = useCurationConfigValue('searchTerms');
    const interstitial = Boolean(useCurationConfigValue('interstitial'));

    const metricFilters = useListeningCurationFilterValues();
    const metricRequests = {
        count: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'count',
            filters: metricFilters
        }
    };
    const {isLoading, error, data: metricResults} = useMetrics(metricRequests);

    const hasError = error || (metricResults && RivalIQ.metrics.new.MetricResponse.isErrorResponse(metricResults.count));
    const count = metricResults?.count?.data?.[0]?.[0];

    const hasNonZeroCount = !isLoading && count > 0;
    const hasZeroCount = !isLoading && count === 0;

    const landscapeName = RivalIQ.state.view.get('landscapeName');
    const subtitle = searchTerms ? `Instant Search` : `${exportTitle}: ${topicName}`;
    const isPDFExport = useIsExportingPDF();
    return (
        <>
            <ExportHeaders
                title={`Listening`}
                subtitles={[
                    `Landscape: ${landscapeName}`,
                    `${subtitle}`
                ]}
                timePeriodStr={timePeriodStr}
            />

            {!isExporting &&
                <div className='tlw-mb-10'>
                    <div className='tlw-flex tlw-flex-col tlw-gap-8 sm_tlw-flex-row'>
                        <Toolbar
                            className='tlw-grow'
                        />

                        <ExportDropdown
                            variant='large'
                            instanceId={showOnlyInstanceId} />
                    </div>
                </div>
            }

            {hasError && <RIQPageError/>}

            {isLoading && <Loading/>}

            {hasZeroCount &&
                <RIQPageError>
                    <p>
                        There were no matching posts for your search.
                        {!isExporting && <> Try broadening your search query or removing filters.</>}
                    </p>
                </RIQPageError>
            }

            <div
                className={hasNonZeroCount ? '' : 'tlw-invisible'} // hide the dashboard if there are no posts
            >
                {widgetGroupsToRender.flatMap((widgetGroup, index) => {
                    const numWidgets = widgetGroup.widgets.length;
                    const widgets = _.map(widgetGroup.widgets, widget => {
                        return React.createElement(widget.widgetClass, _.defaults({
                            key: widget.instanceId,
                            instanceId: widget.instanceId,
                            pdfExportConfig: {
                                exportOrientation: RivalIQ.enums.orientation.LANDSCAPE,
                                exportWidth: 970
                            },
                            appSpecificExportConfig: {
                                title: `Listening`,
                                appName: topicName ? exportTitle : 'Instant Search',
                                // in order to not duplicate code, we're shoving things into existing spaces (see baseInstagramInsights.js getTitleSlide)
                                focusCompanyTitle: topicName ? exportTitle : 'Instant Search',
                                focusCompany: topicName,
                                timePeriodStr: timePeriodStr
                            }
                        }, widget.props));
                    });
                    if (numWidgets === 1) {
                        if (index > 0 && isPDFExport) {
                            return (
                                <div className='tlw-break-before-page'>
                                    {widgets[0]}
                                </div>
                            );
                        }
                        return widgets;
                    } else if (isPDFExport) {
                        return (
                            _.map(widgets, single => {
                                return (
                                    // the grid classes here seem useless but puppeteer works in mysterious ways
                                    // with no grid or a single column, the widgets look like landscape mode shoved into a portrait page
                                    // with just two columns, each widget only takes up half of the page width
                                    // basically, we're tricking puppeteer into doing the "right thing"
                                    <div className='tlw-grid tlw-break-before-page tlw-grid-cols-1 md_tlw-grid-cols-2'>
                                        {single}
                                    </div>
                                );
                            })
                        );
                    } else {
                        return (
                            <div
                                key={index}
                                className='tlw-grid tlw-grid-cols-1 md_tlw-grid-cols-2 md_tlw-gap-10'
                            >
                                {widgets}
                            </div>
                        );
                    }
                })}
            </div>

            {!hasZeroCount && !hasError &&
                <>
                    <InstantSearchInfoModal
                        isAnyWidgetLoaded={isAnyWidgetLoaded}
                    />
                    <GeneratingResultsModal
                        hidden={!interstitial}
                        isAnyWidgetLoaded={isAnyWidgetLoaded}
                        listeningQueryId={listeningQueryId}
                    />
                </>
            }
        </>
    );
}

function CurationLayout() {
    const hasShownAlert = useRef(true);
    const {info: alertInfo} = useShellAlert();
    const setTimePeriod = useSetCurationConfigState(null, 'timePeriod');

    const companyId = useCurationConfigValue('companyId');
    const plan = useCurationConfigValue('plan');
    const listeningQueryId = useCurationConfigValue('listeningQueryId');
    const searchTerms = useCurationConfigValue('searchTerms');
    const showAdvancedSearchEditor = useCurationConfigValue('showAdvancedSearchEditor');

    const {data: listeningQuery} = useLoadLandscapeListeningQuery(listeningQueryId);
    const topicId = listeningQuery?.definition?.topicId;
    const setTopicId = useSetCurationConfigState('topicId');
    const isSampleTopic = Boolean(listeningQuery?.isSampleTopic);

    useEffect(() => {
        setTopicId(topicId);
    }, [setTopicId, topicId]);

    const showNoSearchTermView = !listeningQueryId && !topicId && _.isEmpty(searchTerms) && !showAdvancedSearchEditor && !companyId;

    const isNewSearch = !listeningQueryId && !topicId && _.isEmpty(searchTerms) && !companyId;

    const curationTitle = useGetCurationTitle();
    const title = isNewSearch && showAdvancedSearchEditor ? 'Instant Search' : curationTitle;

    const timezone = useCurationConfigValue('timezone');
    const timeIntervalResponse = useGetListeningQueryTimePeriod(listeningQuery, listeningQueryId, timezone);

    const {
        description: timePeriodDescription,
        label: timePeriodStr,
        message: timePeriodMessage,
        override: timePeriodOverride,
        period: timePeriod,
        restrictions: dateRangeRestrictions
    } = useListeningSearchTimePeriod(isSampleTopic, showNoSearchTermView, timeIntervalResponse);

    useEffect(() => {
        if (hasShownAlert.current && timePeriodOverride) {
            hasShownAlert.current = false;
            setTimePeriod(timePeriodOverride);
            alertInfo(listeningDefinitions.shell.timePeriodChange(timePeriod));
        }
    }, [alertInfo, setTimePeriod, timePeriod, timePeriodOverride]);

    const shellPrimaryConfiguration = useMemo(() => {
        const configuration = {
            companySets: {
                disabled: true
            },
            dataFreshness: {
                disabled: true
            },
            timePeriodDateRangeRestrictions: dateRangeRestrictions,
            timePeriod: timePeriod,
            timePeriodMessage: timePeriodMessage,
            timePeriodDescription: timePeriodDescription
        };

        if (listeningQueryId) {
            const listeningQueryTypeId = listeningQuery?.listeningQueryTypeId;
            configuration.sidebar = {
                id: (listeningQueryTypeId === RivalIQ.enums.listeningQueryTypes.QuidTopic.id ? 'social_listening_quid_topics' : 'social_listening_saved_topics')
            };
        }
        return configuration;
    }, [dateRangeRestrictions, timePeriod, timePeriodDescription, timePeriodMessage, listeningQuery, listeningQueryId]);

    useUpdateShellPrimary(title, shellPrimaryConfiguration);

    useUpdateShellSecondary(false);

    const updateLoadingStatus = useSetInstanceLoadingStatus();

    useLandscapeChangeRedirectUrl('/listening/search');

    const onlyInstanceId = useCurationConfigValue('onlyInstanceId');
    const showOnlyInstanceId = useGetFocusedInstanceIdValue() || onlyInstanceId;

    const hasFullInstantSearch = plan.has_full_instant_search;

    const {validationState} = useVerifySearch(!_.isEmpty(searchTerms), {
        searchTerms: searchTerms
    });

    useEffect(() => {
        updateLoadingStatus(LoadingStatus.COMPLETE);
    }, [updateLoadingStatus]);

    const isExporting = useIsExporting();
    const companyIdForQueryGen = !showAdvancedSearchEditor && _.isEmpty(searchTerms) ? companyId : null;
    const {error: queryGenError, isLoading: isGeneratingQuery, query} = useGenerateCompanyQuery(companyIdForQueryGen);
    const propsSetter = useUpdateProps();
    const listeningSearchTracking = useListeningSearchTracking();
    const markModalEligibleToShow = useSetShowInstantSearchModal()[1];
    useEffect(() => {
        if (companyId && query) {
            listeningSearchTracking(query);
            markModalEligibleToShow(true);

            propsSetter(query);
        }
    }, [companyId, listeningSearchTracking, query, propsSetter, markModalEligibleToShow]);

    const showQueryGenError = companyIdForQueryGen && (queryGenError || (!isGeneratingQuery && !query));
    const showDashboard = !showNoSearchTermView && (validationState !== ValidationStates.Invalid || Boolean(listeningQueryId) || Boolean(topicId)) && !showAdvancedSearchEditor && !showQueryGenError;
    const showError = !showDashboard && !showNoSearchTermView && !showAdvancedSearchEditor && !companyId && !showQueryGenError;

    const useStaggeredLoad = !isExporting && !showOnlyInstanceId && hasFullInstantSearch && showDashboard;

    const initialWidgetIdsToLoad = getWidgetIds(getWidgetGroups(false, hasFullInstantSearch, isSampleTopic, showOnlyInstanceId, isExporting));
    const initialWidgetLoadStatuses = useGetInstanceLoadingStatuses(initialWidgetIdsToLoad);
    const initialWidgetsInitiated = _.every(initialWidgetLoadStatuses, status => status === LoadingStatus.LOADING || status === LoadingStatus.COMPLETE);
    const initialWidgetsLoaded = _.every(initialWidgetLoadStatuses, status => status === LoadingStatus.COMPLETE);
    const [initialWidgetsMetricLoadsInitiated, setInitialWidgetsMetricLoadsInitiated] = useState(!useStaggeredLoad);

    const canLoadRemainingWidgets = !useStaggeredLoad || (initialWidgetsMetricLoadsInitiated && initialWidgetsInitiated);
    const widgetGroupsToRender = getWidgetGroups(canLoadRemainingWidgets, hasFullInstantSearch, isSampleTopic, showOnlyInstanceId, isExporting);

    // This is to stagger the loading of the widgets; with this, we'll show the first 3 widgets immediately, and then after a 250ms delay, we'll show the rest.
    useEffect(() => {
        if (useStaggeredLoad && initialWidgetsInitiated && !initialWidgetsMetricLoadsInitiated) {
            setTimeout(() => {
                setInitialWidgetsMetricLoadsInitiated(true);
            }, 250);
        } else if (!initialWidgetsInitiated && initialWidgetsMetricLoadsInitiated) {
            setInitialWidgetsMetricLoadsInitiated(false);
        }
    }, [useStaggeredLoad, initialWidgetsInitiated, initialWidgetsMetricLoadsInitiated]);

    const allAvailableWidgetIds = getWidgetIds(getWidgetGroups(true, hasFullInstantSearch, isSampleTopic, showOnlyInstanceId, isExporting));
    const exportableWidgetIds = getWidgetIds(getWidgetGroups(true, hasFullInstantSearch, isSampleTopic, showOnlyInstanceId, true));
    const isAnyWidgetLoaded = useGetInstanceLoadingStatuses(allAvailableWidgetIds).some(status => status === LoadingStatus.COMPLETE);

    useShowEditSavedSearchTour(listeningQueryId, showDashboard && initialWidgetsLoaded);

    const listeningQueryDisplayName = useGetListeningQueryDisplayName(listeningQuery, listeningQueryId);
    const exportTitle = listeningQuery?.listeningQueryTypeId === RivalIQ.enums.listeningQueryTypes.QuidTopic.id ? `Quid Monitor Topic` : `Saved Search`;

    useSetExportConfig({
        title: 'Listening',
        timePeriodStr: timePeriodStr,
        supportedExportFormats: [RivalIQ.enums.exportFormat.PNG, RivalIQ.enums.exportFormat.PDF, RivalIQ.enums.exportFormat.PPT],
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        appSpecificExportConfig: {
            exportTitle: listeningQueryId ? `${exportTitle} / ${listeningQueryDisplayName}` : 'Instant Search',
            appName: listeningQueryId ? exportTitle : 'Instant Search',
            // in order to not duplicate code, we're shoving things into existing spaces (see baseInstagramInsights.js getTitleSlide)
            focusCompanyTitle: listeningQueryId ? exportTitle : 'Instant Search',
            focusCompany: listeningQueryDisplayName
        },
        pptExportConfig: {
            showIntroSlide: true,
            slideConfigs: _.map(exportableWidgetIds, instanceId => {
                return {
                    instanceId: instanceId
                };
            })
        }
    });

    useTrackViewedResults(showNoSearchTermView || showQueryGenError || showError, showDashboard);

    const hasCompanionMode = useHasCompanionMode();
    const hasValidQuidMonitorToken = useHasValidQuidMonitorToken();
    if (hasCompanionMode && !hasValidQuidMonitorToken) {
        return <InvalidQuidMonitorTokenErrorPage/>;
    }

    return (
        <>
            {showNoSearchTermView && <NoSearchTerm/>}

            {isGeneratingQuery && <GeneratingQueryModal companyId={companyId}/>}

            {showError &&
                <RIQPageError>
                    <div
                        className='tlw-p-5'
                    >
                        We are having trouble returning the results for this query.
                        <p
                            className='tlw-text-md'
                        >
                            Please&nbsp;
                            <button
                                className='tlw-border-0 tlw-bg-transparent tlw-p-0 tlw-text-blue-400 tlw-outline-none'
                                onClick={() => window.Intercom('show')}
                            >
                                contact us
                            </button>
                            &nbsp;so we can resolve the issue.
                        </p>
                    </div>
                </RIQPageError>
            }

            {showQueryGenError &&
                <RIQPageError>
                    <div
                        className='tlw-p-5'
                    >
                        We are having trouble generating a query for this company.
                        <p
                            className='tlw-text-md'
                        >
                            Please&nbsp;
                            <button
                                className='tlw-border-0 tlw-bg-transparent tlw-p-0 tlw-text-blue-400 tlw-outline-none'
                                onClick={() => window.Intercom('show')}
                            >
                                contact us
                            </button>
                            &nbsp;so we can resolve the issue.
                        </p>
                    </div>
                </RIQPageError>
            }

            {showAdvancedSearchEditor &&
                <>
                    <SearchWizard
                        isEditingInstantSearch={true}
                    />
                </>
            }

            {showDashboard &&
                <ListeningSearchDashboard
                    exportTitle={exportTitle}
                    topicName={listeningQueryDisplayName}
                    timePeriodStr={timePeriodStr}
                    widgetGroupsToRender={widgetGroupsToRender}
                    listeningQueryId={listeningQueryId}
                    showOnlyInstanceId={showOnlyInstanceId}
                    isAnyWidgetLoaded={isAnyWidgetLoaded}
                />
            }
        </>
    );
}

export default function ListeningSearchCuration(props) {

    const listeningQueryId = props?.listeningQueryId;
    const listeningQueryTypeId = props?.listeningQueryTypeId;
    const {data: listeningQuery, isLoading, error: listeningQueryLoadError} = useLoadLandscapeListeningQuery(listeningQueryId, listeningQueryTypeId);
    const isLoadingListeningQuery = listeningQueryId && isLoading;
    const interstitial = Boolean(props?.listeningQueryId && props?.interstitial);

    const defaultProps = {
        instanceId: props?.instanceId,
        interstitial: interstitial,
        scheduleId: props?.scheduleId,
        companyId: props?.companyId,
        graphInterval: 'day',
        wProps: {
            'sentiment-drivers': {
                layout: 'wordcloud',
                driver: 'attributes'
            }
        }
    };

    if (listeningQuery?.props?.language) {
        delete defaultProps.language;
    }

    function disableUrlPersistence(curationProps) {
        return Boolean(curationProps.showAdvancedSearchEditor);
    }

    const configurationSchema = {

        disableUrlPersistence: disableUrlPersistence,

        curationPropsConfig: {
            companyId: {
                urlPersistence: true
            },
            listeningQueryId: {
                urlPersistence: true
            },
            searchTerms: {
                urlPersistence: true
            },
            topicId: {
                urlPersistence: true
            },
            source: {
                urlPersistence: true
            },
            selectedSource: {
                urlPersistence: true
            },
            selectedLanguage: {
                urlPersistence: true
            },
            sentiment: {
                urlPersistence: true
            },
            geo: {
                urlPersistence: true
            },
            language: {
                urlPersistence: true
            },
            searchTermsAnd: {
                urlPersistence: true
            },
            searchTermsNot: {
                urlPersistence: true
            },
            excludeAuthor: {
                urlPersistence: true
            },
            excludeDomain: {
                urlPersistence: true
            },
            theme: {
                urlPersistence: true
            },
            graphInterval: {
                urlPersistence: true
            }
        },
        widgetPropsConfig: {
            'phrase-analysis': {
                layout: {
                    urlPersistence: true
                }
            },
            'domain-analysis': {
                layout: {
                    urlPersistence: true
                }
            },
            'hashtag-analysis': {
                layout: {
                    urlPersistence: true
                }
            },
            'person-analysis': {
                layout: {
                    urlPersistence: true
                }
            },
            'brand-analysis': {
                layout: {
                    urlPersistence: true
                }
            },
            'sentiment-drivers': {
                layout: {
                    urlPersistence: true
                },
                driver: {
                    urlPersistence: true
                }
            },
            'author-analysis': {
                layout: {
                    urlPersistence: true
                }
            }
        }
    };

    const listeningQueryDeleted = listeningQueryId && !listeningQuery && !isLoading && !listeningQueryLoadError;
    const showDashboard = !listeningQueryLoadError && !isLoadingListeningQuery && !listeningQueryDeleted;

    return (
        <SavedTopicContext.Provider
            value={listeningQuery}
        >
            {isLoadingListeningQuery && <RIQLoading />}

            {listeningQueryLoadError && <RIQPageError/>}

            {listeningQueryDeleted &&
                <RIQPageError>
                    <p>
                        We could not find this saved search, perhaps it was deleted?
                    </p>
                </RIQPageError>
            }

            {showDashboard &&
                <ConfigurationProvider
                    defaultProps={defaultProps}
                    configurationSchema={configurationSchema}
                >
                    <CurationLayout />
                </ConfigurationProvider>
            }
        </SavedTopicContext.Provider>
    );
}
